import { type IRenderer, type Method } from '../types';

interface LogMessage {
  timestamp: number;
  method: Method;
  tags: string[];
  data: string[];
}

export class MemoryLogStorage implements IRenderer {
  private static instance: MemoryLogStorage;
  private readonly startMessages: LogMessage[] = [];
  private readonly endMessages: LogMessage[] = [];
  private readonly START_BUFFER_SIZE = 50;
  private readonly END_BUFFER_SIZE = 1950;

  private constructor() {}

  static getInstance(): MemoryLogStorage {
    if (!MemoryLogStorage.instance) {
      MemoryLogStorage.instance = new MemoryLogStorage();
    }
    return MemoryLogStorage.instance;
  }

  private serializeValue(value: any): string {
    try {
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (value instanceof Error) {
        return `Error: ${value.message}\nStack: ${value.stack}`;
      }
      if (value instanceof Date) return value.toISOString();
      if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
      const seen = new Set();
      return JSON.stringify(
        value,
        (key, val) => {
          if (val !== null && typeof val === 'object') {
            if (seen.has(val)) return '[Circular Reference]';
            seen.add(val);
          }
          return val;
        },
        2,
      );
    } catch (err) {
      const error = err as Error;
      return `[Serialization Error: ${error.message}]`;
    }
  }

  write(method: Method, tags: string[], ...rest: any[]) {
    const message: LogMessage = {
      timestamp: Date.now(),
      method,
      tags: [...tags],
      data: rest.map((item) => this.serializeValue(item)),
    };

    if (this.startMessages.length < this.START_BUFFER_SIZE) {
      this.startMessages.push(message);
    } else {
      if (this.endMessages.length < this.END_BUFFER_SIZE) {
        this.endMessages.push(message);
      } else {
        this.endMessages.shift();
        this.endMessages.push(message);
      }
    }
  }

  clear(): void {
    this.startMessages.length = 0;
    this.endMessages.length = 0;
  }

  serializeBuffer(): string {
    let result = '';
    this.startMessages.forEach((msg) => {
      result += `[${new Date(msg.timestamp).toISOString()}] ${msg.method} [${msg.tags.join('.')}]: ${msg.data.join(', ')}\n`;
    });
    result += '\n';
    result += '===========================================================\n';
    result += '--------------------------------- End of Start Messages ---\n';
    result += '===========================================================\n';
    result += '\n';
    result += '\n';
    result += '\n';
    result += '\n';
    result += '\n';
    result += '\n';
    this.endMessages.forEach((msg) => {
      result += `[${new Date(msg.timestamp).toISOString()}] ${msg.method} [${msg.tags.join('.')}]: ${msg.data.join(', ')}\n`;
    });
    return result;
  }
}

export class MemoryBuffer implements IRenderer {
  private storage: MemoryLogStorage;
  constructor() {
    this.storage = MemoryLogStorage.getInstance();
  }
  write(method: Method, tags: string[], ...rest: any[]) {
    this.storage.write(method, tags, ...rest);
  }
}
